// Shared Cashfree checkout — every payment on the platform goes through here:
// guided Services (upfront + balance) and Incubator/VC subscriptions.
//
// Each caller supplies its own pair of backend endpoints:
//   POST orderUrl  → { order_id, payment_session_id, mode } — or { paid | already_paid }
//                    when there is nothing for Cashfree to collect
//   POST verifyUrl { order_id } → { paid, status, invoice }
// The backend fetches the order from Cashfree itself before settling, so the
// browser's checkout result is never treated as proof of payment.
import React from 'react';
import { Receipt } from '@phosphor-icons/react';
import api from '@/api';

let cashfreeSdkPromise;
export const loadCashfreeSdk = () => {
  if (window.Cashfree) return Promise.resolve(window.Cashfree);
  if (cashfreeSdkPromise) return cashfreeSdkPromise;
  cashfreeSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;
    script.onload = () => window.Cashfree
      ? resolve(window.Cashfree)
      : reject(new Error('Cashfree SDK did not initialise'));
    script.onerror = () => reject(new Error('Could not load Cashfree checkout'));
    document.head.appendChild(script);
  });
  return cashfreeSdkPromise;
};

/**
 * Create the order, open Cashfree's modal, then verify server-side.
 * Resolves to { paid, settledWithoutCheckout, status, invoice }; throws on
 * API / SDK errors so callers keep their own error toast.
 */
export const payWithCashfree = async ({ orderUrl, verifyUrl, body = {} }) => {
  const order = await api.post(orderUrl, body);
  // Already settled, or a 100% coupon — nothing to send to Cashfree.
  if (order.data.paid || order.data.already_paid) {
    return { paid: true, settledWithoutCheckout: true, invoice: order.data.invoice };
  }
  const Cashfree = await loadCashfreeSdk();
  await Cashfree({ mode: order.data.mode || 'sandbox' }).checkout({
    paymentSessionId: order.data.payment_session_id,
    redirectTarget: '_modal',
  });
  const verified = await api.post(verifyUrl, { order_id: order.data.order_id });
  return { paid: !!verified.data.paid, status: verified.data.status, invoice: verified.data.invoice };
};

/** If Cashfree redirected back here (`?cashfree_return=1&order_id=…`), verify
 *  that order once and strip the params. Resolves null when not a return. */
export const verifyCashfreeReturn = async (verifyUrl) => {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('order_id');
  if (!params.get('cashfree_return') || !orderId) return null;
  window.history.replaceState({}, '', window.location.pathname);
  const verified = await api.post(verifyUrl, { order_id: orderId });
  return { paid: !!verified.data.paid, status: verified.data.status, invoice: verified.data.invoice };
};

export const cashfreeNotPaidMessage = (status) => (status === 'ACTIVE'
  ? 'Payment was not completed.'
  : `Payment status: ${status || 'pending'}`);

export const CashfreePayButton = ({ amountLabel, paying, onClick, testid, disabled, type = 'button', className = '', style }) => (
  <button type={type} onClick={onClick} disabled={paying || disabled} data-testid={testid} style={style}
    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50 ${className}`}>
    <Receipt size={13} weight="bold" />
    {paying ? 'Opening secure checkout…' : `Pay ${amountLabel} via Cashfree`}
  </button>
);
